import { useEffect, useState } from "react";
import { EventType } from "../../../models/EventType";
import { useEventType } from "../../../contexts/EventTypeContext";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import LogoHorizontal from "../../../components/LogoHorizontal";
import { useTenant } from "../../../contexts/TenantContext";
import { useClient } from "../../../contexts/ClientContext";
import { useAuth } from "../../../contexts/AuthContext";
import { PoweredByLogo } from "../../../components/PoweredByLogo";
import { Heading } from "../../../design/heading";
import { Text } from "../../../design/text";
import PhoneNumberInput from "../../../components/PhoneNumberInput";

interface Props {
  children?: React.ReactNode;
}

const ClientOnboardingDialog: React.FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { currentClient, updateClient } = useClient();
  const [firstName, setFirstName] = useState(currentClient?.firstName ?? "");
  const [lastName, setLastName] = useState(currentClient?.lastName ?? "");
  const [email, setEmail] = useState(currentClient?.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState(
    currentClient?.phoneNumber ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (currentClient) {
      setFirstName(currentClient.firstName ?? "");
      setLastName(currentClient.lastName ?? "");
      setEmail(currentClient.email ?? "");
      setPhoneNumber(currentClient.phoneNumber ?? "");
    }
  }, [currentClient]);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await updateClient(user!.uid, {
        firstName,
        lastName,
        email,
        phoneNumber,
        isOnboarded: true,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const hasAllClientInformation =
    currentClient?.firstName &&
    currentClient?.lastName &&
    currentClient?.email &&
    currentClient?.phoneNumber
      ? true
      : false;

  return (
    <Dialog
      open={(currentClient && !currentClient.isOnboarded) === true}
      onClose={() => {}}
    >
      <DialogTitle className="flex flex-col items-center">
        <LogoHorizontal tenant={tenant} className="h-10 mt-6 mb-6 ml-6" />
      </DialogTitle>
      <DialogDescription className="text-center">
        <Heading>Welcome to the Secure Portal</Heading>
        {`At ${tenant?.name}, we are committed to helping you build a strong matter by ensuring every detail is documented accurately and shared effectively with your legal team. CaseTime is designed to give you clarity and confidence as you navigate your matter. 

      Start by documenting key events, uploading evidence, and keeping track of everything in one secure place. The more details you provide, the stronger your matter will be. Let's get started.`}
      </DialogDescription>
      {!hasAllClientInformation && (
        <DialogBody className="text-center">
          <Text>
            Before we get started, we need to collect some information about
            you. This will help us create a personalized experience for you and
            ensure that your matter is handled efficiently.
          </Text>
          <Fieldset>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>First Name</Label>
                <Input
                  value={firstName}
                  placeholder="First Name"
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input
                  value={lastName}
                  placeholder="Last Name"
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Email</Label>
                <Input
                  value={email}
                  placeholder="Email"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Phone Number</Label>
                <PhoneNumberInput
                  value={phoneNumber}
                  placeholder="Phone Number"
                  onChange={(e) => setPhoneNumber(e)}
                />
              </Field>
            </div>
          </Fieldset>
        </DialogBody>
      )}
      <DialogActions className="flex justify-center">
        <Button
          disabled={isSaving}
          onClick={handleSubmit}
          className="flex w-full"
        >
          Get Started
        </Button>
      </DialogActions>
      <DialogActions className="justify-center">
        <PoweredByLogo />
      </DialogActions>
    </Dialog>
  );
};

export default ClientOnboardingDialog;
